import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router, RouterStateSnapshot } from "@angular/router";
import { AuthenticationService } from '@core/services/authentication.serivce';
import { BaseDetailForm } from '@shared/ui/base-detail-form';
import { AuthUser } from '@shared/interfaces/entities/auth-user.interface';
import { UiGroupControl } from '@shared/ui/controls/ui-group-control.model';
import { TextControl } from '@shared/ui/controls/text-control.model';
import { ToggleControl } from '@shared/ui/controls/toggle-control.model';
import { ViewControl } from '@shared/ui/controls/view-control.model';
import { ViewRowControl } from '@shared/ui/controls/view-row-control.model';
import { PasswordControl } from '../../../shared/ui/controls/password-control.model';
import { SecurityRoleSelect } from '../../../shared/ui/select-controls/security-role.model';
import { OrganizationSelect } from '../../../shared/ui/select-controls/organizations-select.model';
import { ReadOnlyIf, ReadOnlyProperty, RequiredIf, RequiredValidator } from '@shared/ui/control-options/control-option.interface';
import { Permission } from '@core/literals/permission.literal';
import { AuthUserFilter } from '../../../shared/interfaces/select-filters/auth-user-filter.interface';
import { OrganizationFilter } from '../../../shared/interfaces/select-filters/organization-filter.interface';
import { SecurityRole, Services } from '../../../shared/literals/shared.literal';
import { SecurityRoleFilter } from '../../../shared/interfaces/select-filters/security-role-filter.interface';
import { CollectionSelect } from '../../../shared/ui/select-controls/collection-select.model';
import { CollectionFilter } from '../../../shared/interfaces/select-filters/collection-filter.interface';
import { CollectionOrganizationName } from '../../../shared/ui/select-controls/col-org-name-select.model';
import { OnChangeArgs } from '../../../shared/components/select-field/select-field.component';
import { ExpansionPanelComponent } from '@progress/kendo-angular-layout';
import { AuthUserService } from '../services/auth-user.service';
import { HttpErrorResponse } from '@angular/common/http';
import { INotificationService } from '../../../shared/services/inotification.service';
import { CountrySelect } from '../../../shared/ui/select-controls/country-select.model';
import { CountryFilter } from '../../../shared/interfaces/select-filters/country-filter.interface';

@Component({
    selector: 'app-auth-user',
    templateUrl: './auth-user.component.html',
    styleUrls: ['./auth-user.component.css'],
    standalone: false
})
export class AuthUserComponent extends BaseDetailForm<AuthUser> implements OnInit {
  @ViewChildren(ExpansionPanelComponent)
  panels: QueryList<ExpansionPanelComponent>;

  id: string;
  labServices: boolean;

  showSaveButton: boolean;
  showRemoveButton: boolean;
  removeDefaultToolbarItems: boolean;

  securityRoleDescriptions: any = [];

  collection: CollectionOrganizationName;
  constructor(private activatedRouter: ActivatedRoute,
              private auth: AuthenticationService,
              private authUserService: AuthUserService,
              private notificationService: INotificationService,
              private router: Router) {
    super();
    this.name = "User";
    this.apiController = "AuthUser";
    this.keyFieldName = "id";

    this.activatedRouter.params.subscribe(params => this.id = params['id']);

    this.initializeUiControls();
  }

  ngOnInit() {
    var session = this.auth.getSession();

    this.showSaveButton = (this.auth.canPerformAction(Permission.CreateUpdateUsers));
    this.showRemoveButton = (this.auth.canPerformAction(Permission.CreateUpdateUsers)) && !this.isNew();

    this.authUserService.lookupSecurityRole(session?.securityRoleId).subscribe({
      next: (data: Array<SecurityRole>) => {
        if (data != undefined && data.length > 0) {
          this.securityRoleDescriptions = data;
        }
      },
      error: (e: HttpErrorResponse) => {
        var errorMsg: string = e.error;
        if (typeof e.error === "object") {
          errorMsg = e.statusText
        }
        this.notificationService.show({ title: "Error!", description: errorMsg, type: "error" });
      }
    });
  }

  entityLoaded(): void {
    if (this.entity) {
      var session = this.auth.getSession();
      var saveBtn = this.toolbarItems.filter(t => t.text == "Save")[0];
      var removeBtn = this.toolbarItems.filter(t => t.text == "Remove")[0];

      var organizationId = session?.organizationId;

      if (organizationId != undefined && !this.entity.organizationIds.includes(organizationId)) {
          this.router.navigate(['']);
          this.notificationService.show({ title: "Error!", type: "error", description: "You are not allowed to view this due to organization" });
      }

      //Only admins can view admins
      if (this.entity.securityId == 1 && session?.securityRoleId != 1) {
        this.router.navigate(['']);
        this.notificationService.show({ title: "Error!", type: "error", description: "You are not allowed to view this." });
      }

      if (this.entity.isActive == false) {

        this.router.navigate(['/AuthUser']);
        this.notificationService.show({ title: "Error!", type: "error", description: "This user is not active!" });
      }

      this.removeDefaultToolbarItems = !this.auth.canPerformAction(Permission.CreateUpdateUsers);

      //If this is a client role account, they can update other users BUT the cannot update admin
      if (this.entity.securityId == Number(SecurityRole.Administrator) || this.entity.clientAdminCanEdit == true) {
        this.removeDefaultToolbarItems = true;
      }

      //If this is a client role account, they can update other users BUT the cannot update admin
      //But other users need to be able to save their own
      if ((this.entity.clientAdminCanEdit == false && !this.auth.isAdmin()) && (this.router.url != "/AuthUser/Detail/" + this.auth.getSession()?.userId)) { //&& this.entity.securityId == Number(SecurityRole.Administrator)
        this.toolbarItems = [];
      }
    }
  }

  isNew() {
    return this.id == "0";
  }

  

  onOrganizationChange(e: OnChangeArgs) {
    var list = e.value as Array<any>;
    var organizationList = new Array<number>;
    list.map(x => {
      organizationList.push(x.id);
    });
    var filters: CollectionFilter = {
      organizations: organizationList
    };

    if (organizationList.length != 0) {
      this.collection.filters = filters;
    } else {

      var filter: CollectionFilter = {
        organizations: null
      };

      this.collection.filters = filter;
    }
  }


  override initializeUiControls() {
    var session = this.auth.getSession();
    var canEdit = this.auth.canPerformAction(Permission.CreateUpdateUsers) && this.auth.isAdmin();
    var hasClientAdminPermissionToUpdateUsers = this.auth.canPerformAction(Permission.CreateUpdateUsersClientAdmin);
    var isCurrentProfile = this.router.url == "/AuthUser/Detail/" + this.auth.getSession()?.userId;

    var organizationData: Array<any> = [
      { id: session?.organizationId, name: session?.organizationName }
    ];

    const detailsView = new ViewControl("User Details");

    const userDetails = new UiGroupControl("", "User Details");

    const firstName = new TextControl("firstName", "First Name", "");
    const lastName = new TextControl("lastName", "Last Name", "");
    const email = new TextControl("email", "Email", "");
    const securityRole = new SecurityRoleSelect("securityId", "Security Role", null);
    const organizations = new OrganizationSelect("organizations", "Organization(s)", hasClientAdminPermissionToUpdateUsers ? organizationData : new Array<any>);
    organizations.onchange = (args: OnChangeArgs) => { this.onOrganizationChange(args); }

    var collectionFilters: CollectionFilter = {
      organizationId: !this.auth.isAdmin() ? session?.organizationId : null
    };

    this.collection = new CollectionOrganizationName("collectionOrgs", "Collection(s)", new Array<any>);
    this.collection.filters = collectionFilters;

    const country = new CountrySelect("countryIds", "Country", new Array<any>);

    var countryFilter: CountryFilter = {
      organizationId: this.auth.getOrganizationId(),
      showAll: true
    };

    country.filters = countryFilter;

    const password = new PasswordControl("password", "Password", "", true, "confirmPassword");
    const confirmPassword = new PasswordControl("confirmPassword", "Confirm Password", "", false, "");
    const disabled = new ToggleControl("isActive", "Is Active?", null);
    const forcePasswordChange = new ToggleControl("forcePasswordChange", "Force Password Change?", null);

    if (!canEdit) {
      organizations.addOption(new ReadOnlyProperty());

      if (!hasClientAdminPermissionToUpdateUsers) {
        securityRole.addOption(new ReadOnlyProperty());
        disabled.addOption(new ReadOnlyProperty());
        forcePasswordChange.addOption(new ReadOnlyProperty());
        this.collection.addOption(new ReadOnlyProperty());
        country.addOption(new ReadOnlyProperty());
      }
    }

    var organizationFilters: OrganizationFilter = {
      organizationId: this.auth.getOrganizationId()
    };

    var securityFilters: SecurityRoleFilter = {
      filterByClientAdmin: hasClientAdminPermissionToUpdateUsers,
      organizationId: this.auth.getOrganizationId()
    };

    securityRole.filters = securityFilters;

    if (isCurrentProfile && hasClientAdminPermissionToUpdateUsers) {
      securityRole.addOption(new ReadOnlyProperty());
    }

    var securityRoleClientAdminCanEdit = new ReadOnlyIf(() => {
      return ((this.entity != undefined ? (hasClientAdminPermissionToUpdateUsers || !this.entity.clientAdminCanEdit && hasClientAdminPermissionToUpdateUsers) : false));
    });

    //A client admin does have permisison to create/update users but they should NOT be able to edit these fields
    if (hasClientAdminPermissionToUpdateUsers) {
      //Filter user organization
      organizations.filters = organizationFilters;
      organizations.addOption(new ReadOnlyProperty());
    }
    //Client admins are a special case here...they have permission to edit and create users but are limited in what they can do
    //If the user is a client admin AND the user they are editing CAN be edited by a client admin via the security role and organization (check the SecurityRoleOrganization table)
    var clientAdminCanEdit = new ReadOnlyIf(() => {
      return (!this.isNew() && (this.entity != undefined ? !this.entity.clientAdminCanEdit && hasClientAdminPermissionToUpdateUsers && !isCurrentProfile : false));
    });

    firstName.addOption(clientAdminCanEdit);
    lastName.addOption(clientAdminCanEdit);
    email.addOption(clientAdminCanEdit);
    password.addOption(clientAdminCanEdit);
    confirmPassword.addOption(clientAdminCanEdit);
    forcePasswordChange.addOption(clientAdminCanEdit);
    disabled.addOption(clientAdminCanEdit);
    securityRole.addOption(securityRoleClientAdminCanEdit);
    this.collection.addOption(clientAdminCanEdit);
    country.addOption(clientAdminCanEdit);


    var uRow1 = new ViewRowControl();
    uRow1.controls.push(firstName, lastName);

    var uRow2 = new ViewRowControl();
    uRow2.controls.push(email, securityRole);

    var uRow3 = new ViewRowControl();
    uRow3.controls.push(password, confirmPassword);

    var uRow4 = new ViewRowControl();
    uRow4.controls.push(forcePasswordChange, disabled);

    var uRow5 = new ViewRowControl();
    uRow5.controls.push(organizations, this.collection);

    var uRow6 = new ViewRowControl();
    uRow6.controls.push(country);

    userDetails.rows.push(uRow1, uRow2, uRow3, uRow4, uRow5, uRow6);
    detailsView.controls.push(userDetails);

    this.views.push(detailsView);
  }
}


